import {
  buildStepToWorkflowMap,
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  StepToWorkflowMap,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@anduin/shared';

export enum PaymentHistoryWorkflowType {
  HISTORY = 'PAYMENT_HISTORY',
}

export enum PaymentsHistoryStep {
  VIEWED = 'PAYMENT_HISTORY_VIEWED',
  DATE_RANGE_CHANGED = 'PAYMENT_HISTORY_DATE_RANGE_CHANGED',
  AIWYN_ONLY_TOGGLED = 'PAYMENT_HISTORY_AIWYN_ONLY_TOGGLED',
  SEARCHED = 'PAYMENT_HISTORY_SEARCHED',
  COLUMN_FILTER_CHANGED = 'PAYMENT_HISTORY_COLUMN_FILTER_CHANGED',
  SORT_CHANGED = 'PAYMENT_HISTORY_SORT_CHANGED',
  LOAD_MORE = 'PAYMENT_HISTORY_LOAD_MORE',
  EXPORT_CLICKED = 'PAYMENT_HISTORY_EXPORT_CLICKED',
  REFUND_DIALOG_OPENED = 'PAYMENT_HISTORY_REFUND_DIALOG_OPENED',
  REFUND_SUBMITTED = 'PAYMENT_HISTORY_REFUND_SUBMITTED',
  REFUND_SUCCEEDED = 'PAYMENT_HISTORY_REFUND_SUCCEEDED',
  REFUND_FAILED = 'PAYMENT_HISTORY_REFUND_FAILED',
  REFUND_CANCELLED = 'PAYMENT_HISTORY_REFUND_CANCELLED',
}

export type PaymentHistorySurface = 'STANDALONE' | 'CLIENT_DETAIL';

export interface PaymentHistoryBaseMetadata {
  aiwynPaymentsOnly: boolean;
  dateRangeDays: number | null;
  surface: PaymentHistorySurface;
  activeFilters: string;
}

export interface PaymentHistoryDateRangeChangedMetadata extends PaymentHistoryBaseMetadata {
  startDate: string;
  endDate: string;
}

export interface PaymentHistoryAiwynOnlyToggledMetadata extends PaymentHistoryBaseMetadata {
  enabled: boolean;
}

export interface PaymentHistorySearchedMetadata extends PaymentHistoryBaseMetadata {
  queryLength: number;
}

export interface PaymentHistoryColumnFilterChangedMetadata extends PaymentHistoryBaseMetadata {
  columnId: string;
}

export interface PaymentHistorySortChangedMetadata extends PaymentHistoryBaseMetadata {
  columnId: string;
  direction: 'asc' | 'desc' | null;
}

export interface PaymentHistoryLoadMoreMetadata extends PaymentHistoryBaseMetadata {
  page: number;
  blockSize: number;
}

export interface PaymentHistoryExportClickedMetadata extends PaymentHistoryBaseMetadata {
  rowCountVisible: number;
}

export interface PaymentHistoryRefundMetadata extends PaymentHistoryBaseMetadata {
  paymentId: number | null;
  amount: number;
  paymentMethodType: string;
}

export interface PaymentHistoryRefundSucceededMetadata extends PaymentHistoryRefundMetadata {
  operationDurationMs: number;
}

export interface PaymentHistoryRefundFailedMetadata extends PaymentHistoryRefundMetadata, FailureContext {
  operationDurationMs: number;
}

interface PaymentHistoryStepMetaDataOverrides {
  [PaymentsHistoryStep.VIEWED]: PaymentHistoryBaseMetadata;
  [PaymentsHistoryStep.DATE_RANGE_CHANGED]: PaymentHistoryDateRangeChangedMetadata;
  [PaymentsHistoryStep.AIWYN_ONLY_TOGGLED]: PaymentHistoryAiwynOnlyToggledMetadata;
  [PaymentsHistoryStep.SEARCHED]: PaymentHistorySearchedMetadata;
  [PaymentsHistoryStep.COLUMN_FILTER_CHANGED]: PaymentHistoryColumnFilterChangedMetadata;
  [PaymentsHistoryStep.SORT_CHANGED]: PaymentHistorySortChangedMetadata;
  [PaymentsHistoryStep.LOAD_MORE]: PaymentHistoryLoadMoreMetadata;
  [PaymentsHistoryStep.EXPORT_CLICKED]: PaymentHistoryExportClickedMetadata;
  [PaymentsHistoryStep.REFUND_DIALOG_OPENED]: PaymentHistoryRefundMetadata;
  [PaymentsHistoryStep.REFUND_SUBMITTED]: PaymentHistoryRefundMetadata;
  [PaymentsHistoryStep.REFUND_SUCCEEDED]: PaymentHistoryRefundSucceededMetadata;
  [PaymentsHistoryStep.REFUND_FAILED]: PaymentHistoryRefundFailedMetadata;
  [PaymentsHistoryStep.REFUND_CANCELLED]: PaymentHistoryRefundMetadata;
}

export type PaymentHistoryWorkflowStepMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  PaymentsHistoryStep,
  PaymentHistoryStepMetaDataOverrides
>;

export type PaymentHistoryStepMetadataInput<E extends PaymentsHistoryStep> = Omit<
  PaymentHistoryWorkflowStepMetaDataMap[E],
  keyof PaymentHistoryBaseMetadata
>;

const PAYMENT_HISTORY_WORKFLOW_STEPS = {
  [PaymentHistoryWorkflowType.HISTORY]: PaymentsHistoryStep,
} as const;

export const PAYMENT_HISTORY_STEP_TO_WORKFLOW: StepToWorkflowMap<typeof PAYMENT_HISTORY_WORKFLOW_STEPS> =
  buildStepToWorkflowMap(PAYMENT_HISTORY_WORKFLOW_STEPS);

export const PAYMENT_HISTORY_STEP_TO_EVENT_TYPE: WorkflowEventEventTypeMap<PaymentsHistoryStep> = {
  ...generateEventTypeMapDefaults(PaymentsHistoryStep, EventType.USER),
  [PaymentsHistoryStep.REFUND_SUCCEEDED]: EventType.SYSTEM,
  [PaymentsHistoryStep.REFUND_FAILED]: EventType.ERROR,
};
